import { makeStyles } from '@material-ui/core/styles';
import { useMutation } from '@tanstack/react-query';
import { Grid, Box } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import clsx from 'clsx';
import { SButton } from '@setvi/shared/components';

import { useAppContext } from 'Providers/AppProvider/AppContext';
import { UpdateUserDetailsInput } from 'Types/api/globalTypes';
import {
  emailRegex,
  PHONE_REGEX_EXP,
  ONLY_ALPHABETS_CHARACTERS
} from 'Utils/regex';
import {
  updateUserDetailsMutation,
  updateQuotePreviewInfoMutation,
  QuotesQueryKey
} from 'Services';

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFilestack, useSubscribedMutation } from '@setvi/shared/hooks';
import { useQuoteStore } from 'pages/quotes/store';
import { UserInformationForm } from './components';

const useStyles = makeStyles({
  testClassName: {
    opacity: 0.2
  },
  updateButton: {
    height: 40,
    marginTop: '15px'
  },
  fullWidth: {
    width: '100%'
  },
  customWidth: {
    width: 109
  }
});

interface IUserInformations {
  onSubmitted?: () => void;
  label?: string;
  submitButtonText?: string;
  fullWidthSubmitButton?: boolean;
}

export const UserInformation = ({
  onSubmitted,
  label = 'User Information',
  submitButtonText = 'Update',
  fullWidthSubmitButton
}: IUserInformations) => {
  const { user, setUser } = useAppContext();
  const { enqueueSnackbar } = useSnackbar();
  const { fileStackUpload } = useFilestack();
  const classes = useStyles();
  const [searchParams] = useSearchParams();
  const quoteId = searchParams.get('quoteId');
  const { updateQuoteInfo } = useQuoteStore();

  const [file, setFile] = useState<File | null>(null);
  const [mainLoading, setMainLoading] = useState(false);

  const { mutateAsync: updateUserDetails, isLoading: loading } = useMutation(
    updateUserDetailsMutation()
  );

  const { mutateAsync: updateQuote, isLoading: isQuoteLoading } =
    useSubscribedMutation(updateQuotePreviewInfoMutation(), [
      QuotesQueryKey.PREVIEW_PDF,
      QuotesQueryKey.GET_SINGLE
    ]);

  const initialValues: UpdateUserDetailsInput = {
    FirstName: emailRegex.test(user.FirstName) ? '' : user.FirstName,
    LastName: emailRegex.test(user.LastName) ? '' : user.LastName,
    ContactNumber: user.ContactNumber || '',
    Email: user.Email || '',
    Avatar: user?.Photo || '',
    UploadId: '',
    CompanyRole: user?.CompanyRole || ''
  };

  const invalidNumberMessage = 'Phone number is not valid';
  const exceedsLimitNumberMessage = 'Exceeded limit for Phone number';

  const externalUserPhoneNumber = Yup.string()
    .required('Phone number is required')
    .matches(PHONE_REGEX_EXP, invalidNumberMessage)
    .max(128, exceedsLimitNumberMessage);
  const userPhoneNumber = Yup.string()
    .matches(PHONE_REGEX_EXP, invalidNumberMessage)
    .max(128, exceedsLimitNumberMessage);

  const validationSchema = Yup.object({
    FirstName: Yup.string()
      .required('First Name is required')
      .matches(ONLY_ALPHABETS_CHARACTERS, 'First Name can only contain letters')
      .max(100, 'Exceeded limit for First Name'),
    LastName: Yup.string()
      .required('Last Name is required')
      .matches(ONLY_ALPHABETS_CHARACTERS, 'Last Name can only contain letters')
      .max(100, 'Exceeded limit for Last Name'),
    ContactNumber: window.location.origin.includes('middleby')
      ? externalUserPhoneNumber
      : userPhoneNumber,
    Avatar: Yup.string(),
    UploadId: Yup.string(),
    CompanyRole: Yup.string()
  });

  const handleSubmit = async (values: UpdateUserDetailsInput) => {
    setMainLoading(true);
    const initialAvatar = user?.Photo || '';
    const currentAvatar = values.Avatar || '';
    const isAvatarChanged = initialAvatar !== currentAvatar;

    const body: UpdateUserDetailsInput = {
      FirstName: values.FirstName,
      LastName: values.LastName,
      ContactNumber: values.ContactNumber,
      Email: values.Email,
      Avatar: values.Avatar,
      CompanyRole: values.CompanyRole
    };

    if (isAvatarChanged) {
      // If photo was added, send the UploadId
      // If photo was removed (currentAvatar is empty), send empty string
      if (file) {
        const filestackRes = await fileStackUpload({
          file
        });

        if (filestackRes.url) {
          body.UploadId = filestackRes.handle;
          body.Avatar = filestackRes.url;
        }
      } else {
        body.UploadId = '';
        body.Avatar = '';
      }
    }

    const { Data } = await updateUserDetails({ body });

    if (Data) {
      const updatedUser = {
        ...user,
        ...Data,
        Photo: body.Avatar,
        HasPhoto: !!body.Avatar
      };

      setUser(updatedUser);

      // If quoteId exists, also update the quote preview info
      if (quoteId) {
        const preparedByData = {
          fullName: `${body.FirstName} ${body.LastName}`.trim(),
          email: body.Email,
          phoneNumber: body.ContactNumber,
          photo: body.Avatar,
          companyRole: body.CompanyRole
        };

        await updateQuote({
          id: quoteId,
          preparedBy: preparedByData
        });

        // Also update the local quote store
        updateQuoteInfo({
          previewInfo: {
            preparedBy: preparedByData
          }
        });

        // Notify other tabs about the quote update using BroadcastChannel
        const channel = new BroadcastChannel('quote-update-channel');
        channel.postMessage({
          type: 'QUOTE_PREPARED_BY_UPDATED',
          quoteId,
          preparedBy: preparedByData
        });
        channel.close();
      }

      enqueueSnackbar(`Changes has been saved successfully`, {
        variant: 'success'
      });
      onSubmitted?.();
    }

    setMainLoading(false);
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={values => handleSubmit(values)}>
      {({ dirty }) => (
        <Form>
          <UserInformationForm
            label={label}
            disabled={loading || mainLoading || isQuoteLoading}
            userRole={user?.Role}
            onImageUpload={(iFile: File | null) => setFile(iFile)}
          />
          <Grid item xs={12}>
            <Box>
              <SButton
                className={clsx(
                  classes.updateButton,
                  fullWidthSubmitButton
                    ? classes.fullWidth
                    : classes.customWidth
                )}
                disabled={!dirty}
                loading={loading || mainLoading || isQuoteLoading}
                type="submit">
                {submitButtonText}
              </SButton>
            </Box>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};
